/**
 * BESTIX v5.0 — Article Verdict Box
 * Signature editorial verdict at end of articles
 */

import { CheckCircle, AlertCircle, XCircle, Zap, Target, DollarSign, Users, UserX } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ArticleVerdictProps {
  rating: 'go' | 'go-if' | 'skip-if';
  ratingText: string;
  theMoment: string;
  theMove: string;
  theTax?: string;
  whoItsFor: string[];
  whoShouldSkip: string[];
  className?: string;
}

export function ArticleVerdict({
  rating,
  ratingText,
  theMoment,
  theMove,
  theTax,
  whoItsFor,
  whoShouldSkip,
  className,
}: ArticleVerdictProps) {
  const ratingConfig = {
    go: {
      icon: CheckCircle,
      label: 'GO',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      iconColor: 'text-emerald-500',
      labelBg: 'bg-emerald-500',
    },
    'go-if': {
      icon: AlertCircle,
      label: 'GO IF...',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      iconColor: 'text-amber-500',
      labelBg: 'bg-amber-500',
    },
    'skip-if': {
      icon: XCircle,
      label: 'SKIP IF...',
      bg: 'bg-red-50',
      border: 'border-red-200',
      iconColor: 'text-red-500',
      labelBg: 'bg-red-500',
    },
  };

  const config = ratingConfig[rating];
  const RatingIcon = config.icon;

  return (
    <div
      className={cn(
        'rounded-xl border-2 overflow-hidden',
        config.bg,
        config.border,
        className
      )}
    >
      {/* Header */}
      <div className="bg-[var(--color-navy)] px-6 py-4 flex items-center justify-between">
        <h3 className="text-white font-bold uppercase tracking-wide">
          BESTIX Verdict
        </h3>
        <div className={cn('flex items-center gap-2 px-3 py-1 rounded-full text-white font-bold', config.labelBg)}>
          <RatingIcon className="w-5 h-5" />
          {config.label}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Rating Text */}
        <p className="text-lg text-[var(--color-navy)] font-medium">{ratingText}</p>

        {/* The Moment */}
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-full bg-[var(--color-gold)]/20 flex items-center justify-center flex-shrink-0">
            <Zap className="w-5 h-5 text-[var(--color-gold)]" />
          </div>
          <div>
            <h4 className="font-bold text-[var(--color-navy)] uppercase tracking-wide text-sm mb-1">
              The Moment
            </h4>
            <p className="text-gray-700">{theMoment}</p>
          </div>
        </div>

        {/* The Move */}
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-full bg-[var(--color-gold)]/20 flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5 text-[var(--color-gold)]" />
          </div>
          <div>
            <h4 className="font-bold text-[var(--color-navy)] uppercase tracking-wide text-sm mb-1">
              The Move
            </h4>
            <p className="text-gray-700">{theMove}</p>
          </div>
        </div>

        {/* The Tax */}
        {theTax && (
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-[var(--color-gold)]/20 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-[var(--color-gold)]" />
            </div>
            <div>
              <h4 className="font-bold text-[var(--color-navy)] uppercase tracking-wide text-sm mb-1">
                The Tax
              </h4>
              <p className="text-gray-700">{theTax}</p>
            </div>
          </div>
        )}

        {/* Who It's For / Who Should Skip */}
        <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-emerald-500" />
              <h4 className="font-bold text-[var(--color-navy)] uppercase tracking-wide text-sm">
                Who It's For
              </h4>
            </div>
            <ul className="space-y-2">
              {whoItsFor.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <UserX className="w-5 h-5 text-red-500" />
              <h4 className="font-bold text-[var(--color-navy)] uppercase tracking-wide text-sm">
                Who Should Skip
              </h4>
            </div>
            <ul className="space-y-2">
              {whoShouldSkip.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ArticleVerdict;
